<template>
  <base-modal class="modal-teacher" title="Edit Skills" @close="$emit('close')" data-test-id="edit-skills-modal">
    <form class="modal-teacher__form" @submit.prevent="save" data-test-id="edit-skills-form">
      <form-control label="ALSO SPEAK" theme="dark" data-test-id="also-speak-section">
        <div
          v-for="(speak, index) in teacherProfile.speaking_languages"
          :key="speak.code"
          class="modal-teacher__columns"
          data-test-id="also-speak-item"
        >
          <div class="modal-teacher__column">
            <label :for="`also-speak-${index}`">{{ speak.name }}</label>
          </div>
          <div class="modal-teacher__column">
            <voxy-select
              :id="`also-speak-${index}`"
              v-model="model.speaking_languages[index].level"
              :options="levelOptions"
              theme="ghost"
              data-test-id="also-speak-select"
            ></voxy-select>
          </div>
        </div>
      </form-control>

      <form-control label="TOPICS" theme="dark" data-test-id="topics-section">
        <div
          v-for="(topic, index) in selectedTopicsInit"
          :key="index"
          class="modal-teacher__list"
          data-test-id="topic-item"
        >
          <voxy-select
            :id="`also-topic-${index}`"
            v-model="model.selected_topics[index]"
            :options="teacherProfile.possible_topics"
            :placeholder="'Select a topic'"
            theme="ghost"
            clearable
            data-test-id="topic-select"
          ></voxy-select>
        </div>
      </form-control>

      <form-control label="INDUSTRIES" theme="dark" data-test-id="industries-section">
        <div
          v-for="(industry, index) in selectedIndustriesInit"
          :key="index"
          class="modal-teacher__list"
          data-test-id="industry-item"
        >
          <voxy-select
            :id="`also-industry-${index}`"
            v-model="model.selected_industries[index]"
            :options="teacherProfile.possible_industries"
            :placeholder="industry || 'Select an industry'"
            theme="ghost"
            clearable
            data-test-id="industry-select"
          ></voxy-select>
        </div>
      </form-control>

      <form-control label="CERTIFICATE" theme="dark" data-test-id="certificate-section">
        <div class="modal-teacher__columns modal-teacher__checkbox" data-test-id="certificate-item">
          <label data-test-id="certificate-checkbox">
            <input
              type="checkbox"
              name="has_certification_eu_recognized"
              v-model="model.has_certification_eu_recognized"
              id="has_certification_eu_recognized"
              :value="teacherProfile.has_certification_eu_recognized"
            />
            EU recognized
          </label>
        </div>

        <template v-if="restrictedSectionVisible">
          <div class="modal-teacher__columns modal-teacher__checkbox" data-test-id="native-item">
            <label data-test-id="native-checkbox">
              <input
                type="checkbox"
                name="has_flag_native"
                v-model="model.has_flag_native"
                id="has_flag_native"
                :value="teacherProfile.has_flag_native"
              />
              NATIVE
            </label>
          </div>

          <div class="modal-teacher__columns modal-teacher__checkbox" data-test-id="demo-item">
            <label data-test-id="demo-checkbox">
              <input
                type="checkbox"
                name="has_flag_demo"
                v-model="model.has_flag_demo"
                id="has_flag_demo"
                :value="teacherProfile.has_flag_demo"
              />
              DEMO
            </label>
          </div>
        </template>
      </form-control>

      <div class="modal-teacher__submit">
        <p v-if="errors.length" data-test-id="error-message">{{ errors }}</p>
        <voxy-button
          view="primary"
          type="submit"
          size="s"
          :disabled="$v.model.$invalid || isSaving"
          data-test-id="submit-button"
        >
          {{ isSaving ? 'Saving...' : 'Save' }}
        </voxy-button>
      </div>
    </form>
  </base-modal>
</template>

<script>
import { VoxyButton, VoxySelect } from '@voxy-team/vue-voxy';
import { mapActions } from 'vuex';

import FormErrorMapper from '@/mixins/FormErrorMapper';

import BaseModal from '@/components/BaseModal';
import FormControl from '@/components/form/FormControl';

const levelOptions = [0, 1, 2, 3, 4, 5];
const selectedTopicsInit = [...Array(6)];
const selectedIndustriesInit = [...Array(6)];

export default {
  name: 'ModalTeacherSkillInfo',

  components: {
    BaseModal,
    FormControl,
    VoxyButton,
    VoxySelect,
  },

  mixins: [FormErrorMapper],

  props: {
    teacherProfile: {
      default: () => ({}),
      required: true,
      type: Object,
    },
    restrictedSectionVisible: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      levelOptions,
      selectedTopicsInit,
      selectedIndustriesInit,
      isSaving: false,
      hasErrors: false,
      errors: {},
      model: {
        speaking_languages: this.teacherProfile.speaking_languages || [],
        selected_topics: (this.teacherProfile.selected_topics || []).map((topic, index) => {
          return this.teacherProfile.selected_topics[index] || null;
        }),
        selected_industries: (this.teacherProfile.selected_industries || []).map((industry, index) => {
          return this.teacherProfile.selected_industries[index] || null;
        }),
        has_certification_eu_recognized: this.teacherProfile.has_certification_eu_recognized || false,
        has_flag_native: this.teacherProfile.has_flag_native || false,
        has_flag_demo: this.teacherProfile.has_flag_demo || false,
      },
    };
  },

  validations: {
    model: {
      selected_topics: {
        hasAtLeastOneTopic(value) {
          return value.some(topic => topic !== null);
        },
      },
      selected_industries: {
        hasAtLeastOneIndustry(value) {
          return value.some(industry => industry !== null);
        },
      },
    },
  },

  methods: {
    ...mapActions('manageTeachers', {
      updateTeacherProfile: 'UPDATE_TEACHER_PROFILE',
    }),

    formatDataPatch() {
      const formatLanguages = Object.fromEntries(
        this.model.speaking_languages.map(language => {
          return [language.code, language.level];
        }),
      );

      return {
        ...this.model,
        speaking_languages: formatLanguages,
        selected_topics: this.model.selected_topics.filter(topic => topic !== null),
        selected_industries: this.model.selected_industries.filter(industry => industry !== null),
      };
    },

    async save() {
      this.errors = {};

      try {
        await this.updateTeacherProfile({
          teacherHashEmail: this.$route.params.teacherHashEmail,
          value: this.formatDataPatch(),
        });

        this.$emit('close');
      } catch (errors) {
        this.errors = JSON.stringify(errors);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~styles/components/teacher-management-sections';
</style>
